import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { UseFormRegisterReturn } from 'react-hook-form'
import { Lock } from '@/assets/icons'
import StringTooltip from '@/components/ui/StringTooltip'

interface InputFieldFloatingLabelProps {
  label: string
  id: string
  required?: boolean
  error?: string
  disabled?: boolean
  /**
   * When disabled, show the lock affordance used for locked invite emails.
   * Defaults to false. Pass true for permanently locked fields like invite email.
   */
  showLock?: boolean
  inlineMessage?: string
  registration: UseFormRegisterReturn
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  tooltipProps?: {
    title: string
    copyToClipboard?: boolean
  }
}

/**
 * Text field with a floating label
 *
 * An input field with floating label support.
 *
 * UX Behavior:
 * - Label appears centered like a placeholder by default
 * - Label floats above the input when focused or when a value is present
 */
export default function InputFieldFloatingLabel(props: InputFieldFloatingLabelProps) {
  const { label, id, required, error, disabled, showLock = false, inlineMessage, registration, inputProps, tooltipProps } = props
  const errorId = error ? `${id}-error` : undefined
  const helperTextId = inlineMessage ? `${id}-helper` : undefined
  const describedBy = [inputProps?.['aria-describedby'], helperTextId, errorId].filter(Boolean).join(' ') || undefined
  return (
    <div data-ds-component="InputFieldFloatingLabel" className="flex flex-col gap-1">
      <div className="float-label relative">
        <StringTooltip title={tooltipProps?.title || ''} placement="bottom-start" copyToClipboard={tooltipProps?.copyToClipboard}>
          <input
            className={`w-full rounded border-0 bg-surface-field ring-1 ring-inset focus:ring-inset focus:outline-hidden disabled:bg-surface-subtle disabled:text-brand-neutral-700 disabled:cursor-not-allowed ${
              error ? 'ring-border-field-error focus:ring-border-field-error' : 'ring-border-field focus:ring-border-field-active'
            }`}
            id={id}
            placeholder=" " // required to activate :placeholder-shown - without it, the label won't float up when the input has a value
            autoComplete="off"
            aria-invalid={!!error}
            aria-required={required}
            {...registration}
            {...inputProps}
            aria-describedby={describedBy}
            disabled={disabled}
          />
        </StringTooltip>
        <label htmlFor={id} className="text-text-tertiary">
          {label}
          {required && <span className="text-red-700 ml-[2px]">*</span>}
        </label>
        {/* Disabled email mask and lock icon */}
        {!!disabled && showLock && (
          <>
            <div
              className="absolute inset-0 rounded pointer-events-none ring-1 ring-inset ring-brand-neutral-300"
              style={{
                background: 'linear-gradient(to right, transparent 0%, transparent 70%, #F6F3ED 90%)',
              }}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <Lock className="h-6 w-6 text-text-tertiary cursor-not-allowed" />
            </div>
          </>
        )}
      </div>
      {error && (
        <div id={errorId}>
          <InlineMessage variant="error">{error}</InlineMessage>
        </div>
      )}
      {!!inlineMessage && (
        <div id={helperTextId}>
          <InlineMessage variant="neutral">{inlineMessage}</InlineMessage>
        </div>
      )}
    </div>
  )
}
